import {
  ENV,
  API_BASE_URL,
  AUTH_BASE_URL,
  USE_MOCK,
  SESSION_KEYS,
  setSessionHospital,
  setSessionClinic,
  getActiveHospitalId,
  getActiveClinicId,
} from './config';

export {
  ENV,
  API_BASE_URL,
  AUTH_BASE_URL,
  USE_MOCK,
  SESSION_KEYS,
  setSessionHospital,
  setSessionClinic,
  getActiveHospitalId,
  getActiveClinicId,
};

// System & Domain Constants
export const APPOINTMENT_STATUSES = {
  BOOKED: 'BOOKED',
  CONFIRMED: 'CONFIRMED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
};

export const CONSULTATION_STATUSES = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  HOSPITAL_ADMIN: 'HOSPITAL_ADMIN',
  SUPER_DOCTOR: 'SUPER_DOCTOR',
  DOCTOR: 'DOCTOR',
  SUPER_RECEPTIONIST: 'SUPER_RECEPTIONIST',
  RECEPTIONIST: 'RECEPTIONIST',
  SUPER_NURSE: 'SUPER_NURSE',
  NURSE: 'NURSE',
  PHARMACIST: 'PHARMACIST',
  LAB_TECH: 'LAB_TECH',
};

export const SYSTEM_ROLES = [
  { value: 'HOSPITAL_ADMIN', label: 'Hospital Administrator', description: 'Full administrative rights for hospital clinics, staff, and operations' },
  { value: 'SUPER_DOCTOR', label: 'Super Doctor / Chief Medical Officer', description: 'Senior physician with consultation, queue management, reports, and oversight' },
  { value: 'DOCTOR', label: 'Doctor / Physician', description: 'Outpatient consultations, clinical notes, and drug prescriptions' },
  { value: 'SUPER_RECEPTIONIST', label: 'Super Receptionist / Frontdesk Lead', description: 'Patient registration, appointment scheduling, billing, and queue management' },
  { value: 'RECEPTIONIST', label: 'Receptionist / Front Desk', description: 'Patient registration, appointment booking, and check-in desk' },
  { value: 'SUPER_NURSE', label: 'Super Nurse / Nursing Lead', description: 'Vitals recording, triage coordination, and clinical floor oversight' },
  { value: 'NURSE', label: 'Staff Nurse', description: 'Patient vitals recording and triage assistance' },
  { value: 'PHARMACIST', label: 'Pharmacist', description: 'Prescription dispensing and medication inventory' },
  { value: 'LAB_TECH', label: 'Lab Technician', description: 'Diagnostic test processing and laboratory report uploads' },
];

export const getRoleBadgeColor = () => {
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

export const DEFAULT_ROLE_PERMISSIONS = {
  SUPER_ADMIN: ['*'],
  ADMIN: ['*'],
  PLATFORM_ADMIN: ['*'],
  HOSPITAL_ADMIN: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'PATIENT_DELETE',
    'APPOINTMENT_READ',
    'APPOINTMENT_WRITE',
    'APPOINTMENT_DELETE',
    'OPD_QUEUE_MANAGE',
    'CONSULTATION_READ',
    'CONSULTATION_WRITE',
    'CONSULTATION_ALL_VIEW',
    'REPORT_READ',
    'REPORT_WRITE',
    'USER_READ',
    'USER_WRITE',
    'USER_DELETE',
    'STAFF_MANAGE',
    'ROLE_READ',
    'ROLE_WRITE',
    'CLINIC_READ',
    'CLINIC_ALL_VIEW',
    'FEATURE_SETTINGS_MANAGE',
  ],
  SUPER_DOCTOR: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'APPOINTMENT_READ',
    'APPOINTMENT_WRITE',
    'OPD_QUEUE_MANAGE',
    'CONSULTATION_READ',
    'CONSULTATION_WRITE',
    'CONSULTATION_ALL_VIEW',
    'REPORT_READ',
    'CLINIC_READ',
  ],
  DOCTOR: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'APPOINTMENT_READ',
    'APPOINTMENT_WRITE',
    'OPD_QUEUE_MANAGE',
    'CONSULTATION_READ',
    'CONSULTATION_WRITE',
    'CLINIC_READ',
  ],
  SUPER_RECEPTIONIST: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'PATIENT_DELETE',
    'APPOINTMENT_READ',
    'APPOINTMENT_WRITE',
    'APPOINTMENT_DELETE',
    'OPD_QUEUE_MANAGE',
    'REPORT_READ',
    'CLINIC_READ',
  ],
  RECEPTIONIST: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'APPOINTMENT_READ',
    'APPOINTMENT_WRITE',
    'OPD_QUEUE_MANAGE',
    'CLINIC_READ',
  ],
  SUPER_NURSE: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'PATIENT_WRITE',
    'APPOINTMENT_READ',
    'OPD_QUEUE_MANAGE',
    'REPORT_READ',
    'CLINIC_READ',
  ],
  NURSE: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'APPOINTMENT_READ',
    'OPD_QUEUE_MANAGE',
    'CLINIC_READ',
  ],
  PHARMACIST: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'CONSULTATION_READ',
    'CLINIC_READ',
  ],
  LAB_TECH: [
    'DASHBOARD_OVERVIEW',
    'PATIENT_READ',
    'CONSULTATION_READ',
    'CLINIC_READ',
  ],
};

export const BLOOD_GROUPS = [
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'
];

export const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
];

export const SPECIALIZATIONS = [
  'General Medicine',
  'Cardiology',
  'Pediatrics',
  'Orthopedics',
  'Dermatology',
  'Gynecology & Obstetrics',
  'ENT',
  'Ophthalmology',
  'Neurology',
  'Psychiatry',
];

export const COMMON_MEDICINES = [
  'Paracetamol 500mg (Dolo 650 / Crocin)',
  'Azithromycin 500mg (Azee 500)',
  'Amoxicillin + Clavulanic Acid 625mg (Augmentin)',
  'Cetirizine 10mg (Cetzine)',
  'Pantoprazole 40mg (Pan 40)',
  'Rabeprazole 20mg + Domperidone (Razo-D)',
  'Metformin 500mg SR (Glycomet)',
  'Glimepiride 1mg + Metformin 500mg (Amaryl M)',
  'Amlodipine 5mg (Amlong)',
  'Telmisartan 40mg (Telma 40)',
  'Atorvastatin 10mg (Atorva 10)',
  'Rosuvastatin 10mg (Rosuvas)',
  'Metoprolol Succinate 25mg (Betaloc 25)',
  'Montelukast 10mg + Levocetirizine 5mg (Montair-LC)',
  'Ibuprofen 400mg + Paracetamol 325mg (Combiflam)',
  'Fexofenadine 120mg (Allegra 120)',
  'Ecosprin 75mg',
  'Calcium 500mg + Vitamin D3 (Shelcal 500)',
  'Vitamin B Complex + Zinc (Becosules Z)',
  'Ofloxacin + Ornidazole (O2 Tablet)',
];

export default ENV;
